import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import styles from './css/BloodPressure.module.css';
import newStyles from './css/PapSmear.module.css';
import { ClipboardPlus, Send } from 'lucide-react';

const PapSmear = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    const [consentGiven, setConsentGiven] = useState(false);
    const [lastPapSmearDate, setLastPapSmearDate] = useState('');
    const [historyOfAbnormalResults, setHistoryOfAbnormalResults] = useState(false);
    const [sampleTaken, setSampleTaken] = useState(false);
    const [clinicNotes, setClinicNotes] = useState('');

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                pap_smear: {
                    consentGiven,
                    lastPapSmearDate,
                    historyOfAbnormalResults,
                    sampleTaken,
                    clinicNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(styles.hideSidebar);
        return () => {
            document.body.classList.remove(styles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.bloodPressureContainer}>
            <Navbar />

            <main className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon} aria-hidden="true">
                            <ClipboardPlus />
                        </div>
                        <h1 className={newStyles.title}>Pap Smear</h1>
                    </div>

                    <div className={styles.patientCard}>
                        <div className={styles.avatar} aria-hidden="true">{initials}</div>
                        <div className={styles.patientText}>
                            <div className={styles.patientName}>{patientName}</div>
                            <div className={styles.patientMetaRow}>
                                <span className={styles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={styles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>CONSENT</div>
                        <div className={newStyles.consentCard}>
                            <div className={newStyles.consentRow}>
                                <Checkbox
                                    id="papSmearConsent"
                                    checked={consentGiven}
                                    onChange={() => setConsentGiven((v) => !v)}
                                />
                                <label htmlFor="papSmearConsent" className={newStyles.consentLabel}>
                                    Patient has given informed consent
                                </label>
                            </div>
                        </div>

                        {!consentGiven && (
                            <div className={newStyles.consentActions}>
                                <div className={newStyles.consentHint}>
                                    Consent required before proceeding with test.
                                </div>
                                <button
                                    type="button"
                                    className={newStyles.cancelButton}
                                    onClick={() => window.history.back()}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    {consentGiven && (
                        <>
                            <div className={styles.sectionBlock}>
                                <div className={styles.sectionTitle}>DETAILS</div>
                                <div className={newStyles.readingsRow}>
                                    <div className={newStyles.readingItem}>
                                        <label className={styles.readingLabel}>Last pap smear date</label>
                                        <div className={styles.inputGroup}>
                                            <input
                                                type="date"
                                                value={lastPapSmearDate}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                                        setLastPapSmearDate(value);
                                                    }
                                                }}
                                                className={newStyles.readingInput}
                                                placeholder="YYYY-MM-DD"
                                                min="1900-01-01"
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={newStyles.toggleSection}>
                                <label className={styles.toggleLabel}>History of abnormal results</label>
                                <button
                                    type="button"
                                    className={`${styles.toggleButton} ${historyOfAbnormalResults ? styles.active : ''}`}
                                    onClick={() => setHistoryOfAbnormalResults(!historyOfAbnormalResults)}
                                    aria-pressed={historyOfAbnormalResults}
                                >
                                    <div className={styles.toggleSlider}></div>
                                </button>
                            </div>

                            <div className={newStyles.toggleSection}>
                                <label className={styles.toggleLabel}>Sample Taken</label>
                                <button
                                    type="button"
                                    className={`${styles.toggleButton} ${sampleTaken ? styles.active : ''}`}
                                    onClick={() => setSampleTaken(!sampleTaken)}
                                    aria-pressed={sampleTaken}
                                >
                                    <div className={styles.toggleSlider}></div>
                                </button>
                            </div>

                            <div className={styles.sectionBlock}>
                                <div className={styles.fieldLabel}>Clinic Notes</div>
                                <textarea
                                    value={clinicNotes}
                                    onChange={(e) => setClinicNotes(e.target.value)}
                                    className={styles.clinicalNotes}
                                    placeholder="Enter clinical notes..."
                                    rows={6}
                                />
                            </div>

                            <div className={styles.cardFooter}>
                                <Button
                                    onClick={handleInvoice}
                                    className={styles.invoiceButton}
                                    type="button"
                                >
                                    <Send size={16} />
                                    Invoice
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PapSmear;